/* eslint-disable */
import { useMemo, useState, useEffect } from 'react';
import { Modal, IconButton } from '@mui/material';
import { Close } from '@mui/icons-material';
import { ClipboardPaste, Check, AlertTriangle } from 'lucide-react';

/**
 * PasteExcelBlockModal
 * --------------------
 * Opens when the user clicks "Paste Layout Block from Excel". Lets them
 * Ctrl+V the BOM block (or several blocks stacked together) directly out
 * of Excel and parses them into recipe-section shapes.
 *
 * Bakingo-style BOM block layout that we parse:
 *
 *   Bill of Materials
 *   Doc no: ...    Effective Date: ...
 *   Number of Batches: 1
 *   1   Section Name: KAJU FANCY    Product Name: Dharwad Peda    306.10
 *   Sr No | Ingredients          | Weight | UoM | Cost | Amount
 *   1     | Dharwad Peda Kunda   | 15.6   | KG  | 320  | 4999
 *   2     | Burasugar Powder     | 0.85   | KG  | 60   | 51
 *   Final Yield/ Output | 16.5  | Kg | Total | 5050
 *   Tolerance           |       | g  |
 *   FINAL COST WITHOUT PACKAGING AND GAS (A) | 306.10
 *
 * If the user selects + copies several stacked blocks at once, the parser
 * yields one parsed block per "Product Name:" marker it finds.
 *
 * Props:
 *   show       boolean
 *   onClose    fn()
 *   onConfirm  fn(parsedBlocks[])    — see parseExcelBlocks() shape below
 */

const UNIT_NORMALIZE_RE = /^(kg|kgs|g|gms?|grams?|ml|ltr|l|liters?|pcs?|piece|pieces|plate)$/i;

const normalizeUnit = (raw) => {
	if (!raw) return 'kg';
	const v = String(raw).trim().toLowerCase();
	if (v === 'kgs' || v === 'kg') return 'kg';
	if (v === 'g' || v === 'gm' || v === 'gms' || v === 'gram' || v === 'grams') return 'g';
	if (v === 'l' || v === 'liter' || v === 'liters' || v === 'ltr') return 'ltr';
	if (v === 'ml') return 'ml';
	if (v === 'pc' || v === 'pcs' || v === 'piece' || v === 'pieces' || v === 'plate') return 'pcs';
	return v;
};

/**
 * Parser. Input: raw clipboard string from Excel (rows = \n, cells = \t).
 * Output: array of parsed blocks:
 *   {
 *     productName, sectionName,
 *     batchSize, batchUnit,
 *     tolerance, toleranceUnit,
 *     ingredients: [{ name, weight, unit }],
 *   }
 */
// Parse one "column strip" of rows — i.e. one BOM block's worth of cells
// per row. parseExcelBlocks slices the full grid into per-block strips
// when side-by-side BOMs are pasted, then dispatches each strip here.
const parseColumnStrip = (rows) => {
	const blocks = [];
	let current = null;
	let inIngredients = false;

	const finalize = () => {
		if (current && (current.productName || current.ingredients.length > 0)) {
			blocks.push(current);
		}
		current = null;
		inIngredients = false;
	};

	for (let i = 0; i < rows.length; i++) {
		const cells = rows[i];
		if (!cells.length || cells.every((c) => !c)) continue;

		// --- "Product Name:" → start a new block ---
		const productNameCell = cells.find((c) => /product name\s*:/i.test(c));
		if (productNameCell) {
			finalize();
			const productName = productNameCell.replace(/.*product name\s*:\s*/i, '').trim();
			const sectionNameCell = cells.find((c) => /section name\s*:/i.test(c));
			const sectionName = sectionNameCell
				? sectionNameCell.replace(/.*section name\s*:\s*/i, '').trim()
				: '';
			current = {
				productName,
				sectionName,
				batchSize: 0,
				batchUnit: 'kg',
				tolerance: 0,
				toleranceUnit: '',
				ingredients: [],
			};
			inIngredients = false;
			continue;
		}

		// --- Ingredients column header → ingredient rows follow ---
		const isHeader =
			cells.some((c) => /^sr\s*no$/i.test(c)) &&
			cells.some((c) => /^ingredients?$/i.test(c)) &&
			cells.some((c) => /^weight$/i.test(c));
		if (isHeader) {
			inIngredients = true;
			continue;
		}

		// --- Final Yield/Output → batch size ---
		if (cells.some((c) => /final yield/i.test(c))) {
			inIngredients = false;
			if (current) {
				// Pick the first numeric cell as the yield value, and the
				// first unit-like cell as the unit.
				let yieldVal = 0;
				let yieldUnit = current.batchUnit || 'kg';
				for (let k = 1; k < cells.length; k++) {
					const c = cells[k];
					if (!c) continue;
					const n = parseFloat(c.replace(/,/g, ''));
					if (!Number.isNaN(n) && yieldVal === 0) yieldVal = n;
					if (UNIT_NORMALIZE_RE.test(c)) yieldUnit = normalizeUnit(c);
				}
				current.batchSize = yieldVal;
				current.batchUnit = yieldUnit;
			}
			continue;
		}

		// --- Tolerance line ---
		if (cells.some((c) => /^tolerance$/i.test(c))) {
			if (current) {
				let tolVal = 0;
				let tolUnit = '';
				for (let k = 1; k < cells.length; k++) {
					const c = cells[k];
					if (!c) continue;
					const n = parseFloat(c.replace(/,/g, ''));
					if (!Number.isNaN(n) && tolVal === 0) tolVal = n;
					if (UNIT_NORMALIZE_RE.test(c)) tolUnit = normalizeUnit(c);
				}
				current.tolerance = tolVal;
				current.toleranceUnit = tolUnit;
			}
			continue;
		}

		// --- Final cost / closing marker → push current block ---
		if (cells.some((c) => /final cost/i.test(c))) {
			finalize();
			continue;
		}

		// --- Section meta lines we want to skip ---
		if (cells.some((c) => /^bill of materials$/i.test(c))) continue;
		if (cells.some((c) => /^doc no/i.test(c))) continue;
		if (cells.some((c) => /^number of batches/i.test(c))) continue;

		// --- Ingredient row ---
		if (inIngredients && current) {
			// Expected layout: [Sr No, Name, Weight, UoM, Cost, Amount]
			// Be tolerant — pull the first numeric cell as weight and the
			// first text cell after Sr No as the ingredient name.
			const srNoRaw = cells[0] || '';
			const name = (cells[1] || '').trim();
			const weight = parseFloat((cells[2] || '').replace(/,/g, ''));
			const uom = cells[3] || '';
			// Skip if the row clearly isn't an ingredient (no name OR weight isn't a number)
			if (!name) continue;
			if (Number.isNaN(weight)) continue;
			// Skip Sr No that isn't a number — defends against accidental footer rows
			if (srNoRaw && Number.isNaN(parseInt(srNoRaw, 10))) continue;
			current.ingredients.push({
				name,
				weight,
				unit: normalizeUnit(uom) || 'kg',
			});
		}
	}

	finalize();
	return blocks;
};

// Top-level entry. Splits the pasted grid into column-strips when multiple
// BOM blocks are pasted side-by-side (each with its own "Sr No" header
// column), then runs parseColumnStrip on each strip.
//
// Single-block pastes and vertically-stacked pastes flow through unchanged
// — parseColumnStrip handles stacking via the "Product Name:" / finalize
// machinery.
export const parseExcelBlocks = (raw) => {
	if (!raw || typeof raw !== 'string') return [];
	const lines = raw.split(/\r?\n/);
	const rows = lines.map((line) => line.split('\t').map((c) => (c || '').trim()));

	// Detect distinct block positions across all rows. Primary signal is
	// the "Sr No" header column; fallback is the "Product Name:" column
	// (used when one block has a non-standard ingredient header so Sr No
	// only fires in the OTHER block, which would otherwise hide the
	// first block from us).
	const srNoColumns = new Set();
	const productNameColumns = new Set();
	for (let i = 0; i < rows.length; i++) {
		for (let c = 0; c < rows[i].length; c++) {
			const cell = rows[i][c];
			if (/^sr\s*no$/i.test(cell)) srNoColumns.add(c);
			if (/product name\s*:/i.test(cell)) productNameColumns.add(c);
		}
	}

	// Sr No header column = start of a block. Use them as block-start
	// boundaries directly.
	let startCols = [];
	if (srNoColumns.size >= 2) {
		startCols = Array.from(srNoColumns).sort((a, b) => a - b);
	} else if (productNameColumns.size >= 2) {
		// Fallback: Product Name is mid-block, not at the start. Take the
		// midpoint between adjacent Product Name cols as the split point
		// so each block keeps its image / "Bill of Materials" / Doc no
		// header rows.
		const pnCols = Array.from(productNameColumns).sort((a, b) => a - b);
		startCols = [0];
		for (let i = 1; i < pnCols.length; i++) {
			startCols.push(Math.floor((pnCols[i - 1] + pnCols[i]) / 2));
		}
	}

	if (startCols.length <= 1) {
		return parseColumnStrip(rows);
	}

	const allBlocks = [];
	for (let i = 0; i < startCols.length; i++) {
		const startCol = startCols[i];
		const endCol = i < startCols.length - 1
			? startCols[i + 1]
			: Number.MAX_SAFE_INTEGER;
		const stripRows = rows.map((row) => (
			row.slice(startCol, endCol === Number.MAX_SAFE_INTEGER ? row.length : endCol)
		));
		allBlocks.push(...parseColumnStrip(stripRows));
	}
	return allBlocks;
};

const SAMPLE_PASTE = `Bill of Materials
Doc no: PRD/BOM/01\tEffective Date: 24/07/2024\t\tRevision date & No
\tNumber of Batches: 1
1\tSection Name: KAJU FANCY\tProduct Name: Dharwad Peda
Sr No\tIngredients\tWeight\tUoM\tCost\tAmount
1\tDharwad Peda Kunda\t15.6\tKG\t320.5\t4999.8
2\tBurasugar Powder\t0.85\tKG\t60\t51
Final Yield/ Output\t\t16.5\tKg\tTotal\t5050.8
Tolerance\t\t\tg
FINAL COST WITHOUT PACKAGING AND GAS (A)\t306.11`;

const PasteExcelBlockModal = (props) => {
	const { show, onClose, onConfirm } = props;

	const [text, setText] = useState('');

	useEffect(() => {
		if (show) setText('');
	}, [show]);

	const parsed = useMemo(() => parseExcelBlocks(text), [text]);
	const totalIngredients = parsed.reduce((s, b) => s + b.ingredients.length, 0);

	if (!show) return null;

	const handleConfirm = () => {
		if (parsed.length === 0) return;
		onConfirm(parsed);
	};

	const handleLoadSample = () => {
		setText(SAMPLE_PASTE);
	};

	return (
		<Modal
			open={show}
			onClose={(e, reason) => {
				if (reason === 'backdropClick') return;
				onClose();
			}}
			aria-labelledby='paste-excel-block-modal'
		>
			<div
				className='absolute top-1/2 left-1/2 bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden focus:outline-none'
				style={{
					width: '60rem',
					maxWidth: '95vw',
					height: '85vh',
					transform: 'translate(-50%, -50%)',
				}}
			>
				{/* Header */}
				<div className='shrink-0 flex justify-between items-center px-6 py-3 border-b border-slate-200 bg-white gap-4'>
					<div className='min-w-0'>
						<h3 className='text-lg font-bold text-slate-900 truncate flex items-center gap-2'>
							<ClipboardPaste className='size-5 text-indigo-600' />
							Paste BOM Block from Excel
						</h3>
						<p className='text-xs text-slate-500 mt-0.5'>
							Select a BOM block in Excel (header row through "FINAL COST" line), copy with Ctrl+C, then paste below. Multiple blocks pasted together will each become a section.
						</p>
					</div>
					<IconButton onClick={onClose} size='small'>
						<Close className='text-slate-500' />
					</IconButton>
				</div>

				{/* Body */}
				<div className='flex-1 overflow-hidden flex flex-col bg-slate-50 p-4 gap-4'>
					{/* Textarea */}
					<div className='flex flex-col gap-2 flex-1 min-h-0'>
						<div className='flex items-center justify-between'>
							<label className='text-[12px] font-semibold text-slate-700 uppercase tracking-wide'>
								Paste Excel cells here
							</label>
							<button
								type='button'
								onClick={handleLoadSample}
								className='text-[11px] text-indigo-600 hover:underline'
							>
								Load sample
							</button>
						</div>
						<textarea
							value={text}
							onChange={(e) => setText(e.target.value)}
							placeholder='Click here and press Ctrl+V…'
							className='flex-1 min-h-0 w-full p-3 border border-slate-300 rounded-md font-mono text-[12px] bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500 resize-none'
						/>
					</div>

					{/* Preview */}
					<div className='shrink-0 max-h-[40%] overflow-y-auto border border-slate-200 rounded-md bg-white'>
						<div className='px-3 py-2 bg-indigo-50 border-b border-slate-200 sticky top-0 flex items-center justify-between'>
							<span className='text-[12px] font-semibold text-indigo-900'>
								Preview — {parsed.length} block{parsed.length !== 1 ? 's' : ''} detected · {totalIngredients} ingredients
							</span>
							{parsed.length === 0 && text && (
								<span className='flex items-center gap-1 text-[11px] text-amber-700'>
									<AlertTriangle className='size-3' />
									Nothing parsed — check the format
								</span>
							)}
						</div>
						{parsed.length === 0 ? (
							<div className='p-4 text-center text-[12px] text-slate-500'>
								Paste a BOM block above to see the parsed preview here.
							</div>
						) : (
							<div className='divide-y divide-slate-200'>
								{parsed.map((b, idx) => (
									<div key={idx} className='p-3'>
										<div className='flex items-center justify-between mb-2'>
											<div className='text-[13px] font-bold text-slate-800'>
												{b.productName || '(no product name)'}
											</div>
											<div className='text-[11px] text-slate-500'>
												Batch: <span className='font-semibold text-slate-700'>{b.batchSize} {b.batchUnit}</span>
												{b.tolerance ? ` · Tol: ${b.tolerance} ${b.toleranceUnit}` : ''}
											</div>
										</div>
										{b.sectionName && (
											<div className='text-[11px] text-slate-500 mb-1'>Section: {b.sectionName}</div>
										)}
										<div className='text-[12px] text-slate-700'>
											{b.ingredients.length === 0 ? (
												<span className='text-amber-700'>No ingredients parsed</span>
											) : (
												<ul className='list-disc pl-5 grid grid-cols-2 gap-x-3 gap-y-0.5'>
													{b.ingredients.map((ing, j) => (
														<li key={j}>
															<span className='font-medium'>{ing.name}</span>
															<span className='text-slate-500'> — {ing.weight} {ing.unit}</span>
														</li>
													))}
												</ul>
											)}
										</div>
									</div>
								))}
							</div>
						)}
					</div>
				</div>

				{/* Footer */}
				<div className='shrink-0 flex justify-end items-center gap-2 px-6 py-3 border-t-2 border-slate-200 bg-white'>
					<button
						type='button'
						onClick={onClose}
						className='px-4 py-2 text-[13px] font-semibold rounded-md bg-white hover:bg-slate-100 text-slate-700 border border-slate-300 transition-colors'
					>
						Cancel
					</button>
					<button
						type='button'
						onClick={handleConfirm}
						disabled={parsed.length === 0}
						className={
							'flex items-center gap-1.5 px-6 py-2 text-[13px] font-bold rounded-md transition-colors ' +
							(parsed.length === 0
								? 'bg-slate-200 text-slate-400 cursor-not-allowed'
								: 'bg-indigo-600 hover:bg-indigo-700 text-white')
						}
					>
						<Check className='size-4' />
						Add {parsed.length > 0 ? `${parsed.length} Section${parsed.length > 1 ? 's' : ''}` : 'Sections'}
					</button>
				</div>
			</div>
		</Modal>
	);
};

export default PasteExcelBlockModal;
